/* eslint-disable camelcase */
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import InvoicePicker from './InvoicePicker'

import styles from '@/pages/app/BillingOps/styles.module.scss'

import { InvoiceSummary } from '@/api/generated'
import Checkbox from '@/components/Checkbox'
import Input from '@/components/Input'
import { RecreateOverrideBundle, RecreateSubscriptionInvoiceInput, StripeAddress } from '@/pages/app/BillingOps/types'

const schema = z
  .object({
    invoice_id: z.string().min(1, 'Invoice ID is required').startsWith('in_', 'Invoice IDs start with "in_"'),
    po_number: z.string().max(140, 'PO number must be 140 characters or fewer'),
    customer_name: z.string(),
    customer_email: z
      .string()
      .refine((v) => v === '' || z.string().email().safeParse(v).success, { message: 'Must be a valid email' }),
    customer_phone: z.string(),
    address_line1: z.string(),
    address_line2: z.string(),
    address_city: z.string(),
    address_state: z.string(),
    address_postal_code: z.string(),
    address_country: z
      .string()
      .refine((v) => v === '' || /^[A-Z]{2}$/.test(v), { message: 'Use a 2-letter ISO country code (e.g. "US")' }),
    applies_to_future_invoices: z.boolean(),
  })
  .superRefine((values, ctx) => {
    const hasAddressField =
      values.address_line1 !== '' ||
      values.address_line2 !== '' ||
      values.address_city !== '' ||
      values.address_state !== '' ||
      values.address_postal_code !== '' ||
      values.address_country !== ''
    const hasCustomerLevelChange =
      values.customer_name !== '' || values.customer_email !== '' || values.customer_phone !== '' || hasAddressField
    const hasAnyChange = values.po_number !== '' || hasCustomerLevelChange

    if (!hasAnyChange) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['po_number'],
        message: 'At least one override (PO or a customer field) is required',
      })
    }
    if (hasCustomerLevelChange && !values.applies_to_future_invoices) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['applies_to_future_invoices'],
        message: 'Customer-level changes apply to all future invoices for this customer — confirm to continue',
      })
    }
  })

type FormValues = z.infer<typeof schema>

const ADDRESS_KEYS = [
  'address_line1',
  'address_line2',
  'address_city',
  'address_state',
  'address_postal_code',
  'address_country',
] as const

const buildAddress = (values: FormValues): StripeAddress | undefined => {
  const address: StripeAddress = {}
  if (values.address_line1 !== '') address.line1 = values.address_line1
  if (values.address_line2 !== '') address.line2 = values.address_line2
  if (values.address_city !== '') address.city = values.address_city
  if (values.address_state !== '') address.state = values.address_state
  if (values.address_postal_code !== '') address.postal_code = values.address_postal_code
  if (values.address_country !== '') address.country = values.address_country
  return Object.keys(address).length === 0 ? undefined : address
}

const buildOverrides = (values: FormValues): RecreateOverrideBundle => {
  const overrides: RecreateOverrideBundle = {}
  if (values.po_number !== '') overrides.po_number = values.po_number
  if (values.customer_name !== '') overrides.customer_name = values.customer_name
  if (values.customer_email !== '') overrides.customer_email = values.customer_email
  if (values.customer_phone !== '') overrides.customer_phone = values.customer_phone
  const address = buildAddress(values)
  if (address !== undefined) overrides.customer_address = address
  if (
    overrides.customer_name !== undefined ||
    overrides.customer_email !== undefined ||
    overrides.customer_phone !== undefined ||
    overrides.customer_address !== undefined
  ) {
    overrides.applies_to_future_invoices = true
  }
  return overrides
}

interface RecreateSubscriptionInvoiceFormProps {
  disabled: boolean
  onSubmit: (input: RecreateSubscriptionInvoiceInput) => void
  onChange: () => void
  formId: string
  openInvoice: InvoiceSummary | null | undefined
}

/**
 * The backend op only operates on **unpaid finalized** (open) subscription
 * invoices — draft and paid are explicitly refused — so we surface only the
 * open invoice in the picker. The strong warning reflects what the op actually
 * does: cancel the subscription, recreate it with the same items and cycle
 * anchor, finalize a replacement invoice with the overrides applied.
 *
 * Customer-level fields (name, email, phone, address) propagate to the Customer
 * object and apply to **all future invoices** for the customer — that's a
 * Stripe constraint, not a UI choice. The consent checkbox makes that explicit.
 */
const RecreateSubscriptionInvoiceForm = ({
  disabled,
  onSubmit,
  onChange,
  formId,
  openInvoice,
}: RecreateSubscriptionInvoiceFormProps) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      invoice_id: '',
      po_number: '',
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      address_line1: '',
      address_line2: '',
      address_city: '',
      address_state: '',
      address_postal_code: '',
      address_country: '',
      applies_to_future_invoices: false,
    },
  })

  const watched = useWatch({ control })
  const currentInvoiceId = watched.invoice_id ?? ''
  const invoiceLooksValid = openInvoice !== null && openInvoice !== undefined && currentInvoiceId === openInvoice.id

  const hasAnyAddressField = ADDRESS_KEYS.some((k) => (watched[k] ?? '') !== '')
  const hasCustomerLevelChange =
    (watched.customer_name ?? '') !== '' ||
    (watched.customer_email ?? '') !== '' ||
    (watched.customer_phone ?? '') !== '' ||
    hasAnyAddressField

  const submit = handleSubmit((values) =>
    onSubmit({
      invoice_id: values.invoice_id,
      overrides: buildOverrides(values),
    })
  )

  const renderTextInput = (
    name: keyof FormValues,
    placeholder: string,
    id: string,
    type: 'text' | 'email' = 'text'
  ) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Input
          id={id}
          type={type}
          value={(field.value as string) ?? ''}
          onInput={(text) => {
            field.onChange(text)
            onChange()
          }}
          placeholder={placeholder}
          className={styles.input}
          isDisabled={disabled}
        />
      )}
    />
  )

  return (
    <form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      className={styles.section}
    >
      {!openInvoice && (
        <div className={styles.errorBanner}>
          This cost center has no open subscription invoice to recreate. Use <code>add_po_to_invoice</code> for drafts,
          or wait for the next invoice to finalize.
        </div>
      )}

      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="recreate-invoice-id">
          Invoice ID
        </label>
        <InvoicePicker
          draft={null}
          open={openInvoice}
          selectedId={currentInvoiceId || null}
          onSelect={(invoice) => {
            setValue('invoice_id', invoice.id, { shouldValidate: true })
            onChange()
          }}
        />
        <Controller
          name="invoice_id"
          control={control}
          render={({ field }) => (
            <Input
              id="recreate-invoice-id"
              type="text"
              value={field.value}
              onInput={(text) => {
                field.onChange(text)
                onChange()
              }}
              placeholder="in_1AbCdEfGhIjKlMnO"
              className={styles.input}
              isDisabled={disabled}
            />
          )}
        />
        {errors.invoice_id && <p className={styles.errorText}>{errors.invoice_id.message}</p>}
        {invoiceLooksValid && (
          <div className={styles.warningBanner}>
            Committing will <strong>cancel the subscription</strong> and <strong>recreate it</strong> with the same
            items, billing cycle anchor, tax rates, payment method, and discount. A replacement invoice will be
            finalized with the overrides applied. The customer will receive the new invoice email. There is no double
            charge — Stripe never re-bills the cancelled invoice. Analytics churn signals are suppressed via the{' '}
            <code>cancel_reason=po_recreate</code> sentinel.
          </div>
        )}
      </div>

      <div className={styles.formGroup}>
        <h3 className={styles.sectionTitle}>Invoice-level override</h3>
        <p className={styles.helperText}>Applied to the new replacement invoice only.</p>
        <label className={styles.label} htmlFor="recreate-po-number">
          PO Number
        </label>
        {renderTextInput('po_number', 'PO-2026-0001', 'recreate-po-number')}
        {errors.po_number && <p className={styles.errorText}>{errors.po_number.message}</p>}
      </div>

      <div className={styles.formGroup}>
        <h3 className={styles.sectionTitle}>Customer-level overrides</h3>
        <p className={styles.helperText}>
          Updates the Stripe Customer object. These fields apply to <strong>all future invoices</strong> for this
          customer — Stripe stores them on the Customer, not the invoice.
        </p>

        <label className={styles.label} htmlFor="recreate-customer-name">
          Customer name
        </label>
        {renderTextInput('customer_name', 'Acme, Inc.', 'recreate-customer-name')}

        <label className={styles.label} htmlFor="recreate-customer-email">
          Customer email
        </label>
        {renderTextInput('customer_email', 'billing@acme.com', 'recreate-customer-email', 'email')}
        {errors.customer_email && <p className={styles.errorText}>{errors.customer_email.message}</p>}

        <label className={styles.label} htmlFor="recreate-customer-phone">
          Customer phone
        </label>
        {renderTextInput('customer_phone', '+1 555 555 5555', 'recreate-customer-phone')}

        <label className={styles.label}>Billing address</label>
        {renderTextInput('address_line1', 'Address line 1', 'recreate-address-line1')}
        {renderTextInput('address_line2', 'Address line 2 (optional)', 'recreate-address-line2')}
        {renderTextInput('address_city', 'City', 'recreate-address-city')}
        {renderTextInput('address_state', 'State / region', 'recreate-address-state')}
        {renderTextInput('address_postal_code', 'Postal code', 'recreate-address-postal-code')}
        {renderTextInput('address_country', 'Country (ISO-2, e.g. US)', 'recreate-address-country')}
        {errors.address_country && <p className={styles.errorText}>{errors.address_country.message}</p>}
      </div>

      {hasCustomerLevelChange && (
        <>
          <div className={styles.warningBanner}>
            <strong>Customer-level blast radius:</strong> the fields above will be written to the Stripe Customer
            object. Every invoice this customer receives from now on — not just this one — will inherit the new values.
            If the customer&apos;s real-world details changed, this is what you want. If you only want to correct this
            one invoice, do not change customer-level fields.
          </div>
          <Controller
            name="applies_to_future_invoices"
            control={control}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onChange={() => {
                  field.onChange(!field.value)
                  onChange()
                }}
                label="I understand these changes apply to all future invoices for this customer"
                disabled={disabled}
              />
            )}
          />
          {errors.applies_to_future_invoices && (
            <p className={styles.errorText}>{errors.applies_to_future_invoices.message}</p>
          )}
        </>
      )}
    </form>
  )
}

export default RecreateSubscriptionInvoiceForm
